import { DatasourceSyncStatus } from '../../../../../../models/datasource-sync-details';
import { intervalToDuration } from 'date-fns';

/**
 * Get disabled value for the restart data sync button
 * @param restartDatasyncLoading
 * @param syncStatusLoading
 * @param syncStatus
 */
export const isRestartDisabled = (restartDatasyncLoading: boolean,
                                  syncStatusLoading: boolean,
                                  syncStatus: DatasourceSyncStatus ): boolean => {
  return restartDatasyncLoading ||
      syncStatusLoading ||
      syncStatus === DatasourceSyncStatus.PENDING ||
      syncStatus === DatasourceSyncStatus.RUNNING
}

/**
 * Get color class ans svg name depending on data sync status as per design doc
 * @param syncStatus
 */
export const getDataSyncColorAndSvg = (syncStatus: DatasourceSyncStatus):
    { colorClass: string, svgIcon: string } => {
  switch (syncStatus) {
    case DatasourceSyncStatus.RUNNING:
    case DatasourceSyncStatus.PENDING:
      return { colorClass: 'text-blue-50', svgIcon: 'data_sync_status_pending' };
    case DatasourceSyncStatus.FINISHED:
      return { colorClass: 'text-green-50', svgIcon: 'data_sync_status_finished' };
    case DatasourceSyncStatus.NOT_CONCLUSIVE:
      return { colorClass: 'text-orange-60', svgIcon: 'data_sync_status_inconclusive' };
    case DatasourceSyncStatus.NOT_START_LAST_12H:
      return { colorClass: 'text-neutral-40', svgIcon: 'data_sync_status_nosync12h' };
    case DatasourceSyncStatus.FAILED:
      return { colorClass: 'text-red-50', svgIcon: 'data_sync_status_failed' };
    default:
      return { colorClass: 'text-neutral-variant-40', svgIcon: 'sync' };
  }
}

/**
 * Get the respective CSS class name for the data sync progress bar depending on sync status
 * @param syncStatus
 */
export const getDataSyncProgressBarClass = (syncStatus: DatasourceSyncStatus): string => {
  switch (syncStatus) {
    case DatasourceSyncStatus.PENDING:
    case DatasourceSyncStatus.RUNNING:
      return 'progressBarPending';
    case DatasourceSyncStatus.FAILED:
      return 'progressBarFailed';
    case DatasourceSyncStatus.NOT_CONCLUSIVE:
      return 'progressBarInconclusive';
    case DatasourceSyncStatus.FINISHED:
      return 'progressBarFinished';
    default:
      return 'progressBarNoSync';
  }
}

/**
 * Calculates the duration between restart time and update time and transforms it into a string
 * @param restartTime
 * @param updateTime
 */
export const getElapsedTime = (restartTime: string, updateTime: string): string => {
  let returnString = '';

  if (!restartTime || !updateTime) {
    return '0s elapsed';
  }

  const duration = intervalToDuration({
    start: new Date(restartTime),
    end: new Date(updateTime)
  });

  if (duration?.hours) {
    returnString = duration.hours + 'h';
  }

  if (duration?.minutes) {
    returnString = returnString +  ' ' + duration.minutes + 'min';
  }
  // do not show amount of seconds if the sync is taking hours already
  if (duration?.seconds && !duration?.hours) {
    returnString = returnString + ' ' + duration.seconds + 's';
  }

  if (returnString) {
    returnString = returnString + ' elapsed'
  } else {
    returnString = '0s elapsed'
  }

  return returnString;
}

/**
 * Transform a percentage string into a number, then round to nearest integer
 * @param syncProgress e.g.: 90.00%
 */
export const getNumberFromPercentageString = (syncProgress: string): number => {
  if (!syncProgress) return 0;

  try {
    return Math.round(parseFloat(syncProgress));
  } catch (e) {
    console.error(e)
    return 0;
  }
}


